import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { api, PRIORITIES, STAGES } from "../lib/api";
import { CATEGORIES } from "../lib/perms";
import { toast } from "sonner";
import { upcomingIntakes } from "../lib/intakes";
import { Search, UserPlus, X } from "lucide-react";

export default function NewApplicationDialog({ open, onOpenChange, meta, onCreated, student }) {
  const [form, setForm] = useState({ stage: "New Application" });
  const [picked, setPicked] = useState(null);
  const [newStudent, setNewStudent] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const chosen = student || picked;

  useEffect(() => { if (!open) { setPicked(null); setNewStudent(false); setForm({ stage: "New Application" }); } }, [open]);

  const submit = async (allowDup = false) => {
    if (!chosen && !newStudent) return toast.error("Search and select the student first, or create a new student");
    if (newStudent && !form.student_name) return toast.error("Student name is required");
    if (newStudent && !form.partner_id) return toast.error("Please select the partner (immigration company)");
    if (!form.college_id) return toast.error("Please select a college");
    if (!form.program_category) return toast.error("Please select the program category");
    setSaving(true);
    try {
      const payload = chosen ? { ...form, student_id: chosen.id } : form;
      const r = await api.post("/applications", allowDup ? { ...payload, allow_duplicate: true } : payload);
      toast.success(`Application ${r.data.application_id} created`);
      onCreated?.(r.data);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.code === "duplicate_application") {
        setSaving(false);
        if (window.confirm(`${d.message}\n\nCreate another one anyway?`)) return submit(true);
        return;
      }
      toast.error(e.response?.data?.detail || "Failed to create application");
    } finally { setSaving(false); }
  };

  const users = meta?.users || [];
  const bdms = users.filter(u => ["bdm", "admin"].includes(u.role));
  const officers = users.filter(u => ["admissions_officer", "admin"].includes(u.role));
  const intakes = upcomingIntakes(meta?.intakes, form.intake_id);
  const sel = (k, items, ph, tid) => (
    <Select value={form[k] || ""} onValueChange={v => set(k, v)}>
      <SelectTrigger data-testid={tid}><SelectValue placeholder={ph} /></SelectTrigger>
      <SelectContent>{items.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
    </Select>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="new-app-dialog" className="max-w-3xl bg-white max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="heading-font">Add Application</DialogTitle></DialogHeader>
        <Step n="1" title="Student">
          {chosen ? <StudentChip s={chosen} onClear={student ? null : () => setPicked(null)} />
            : newStudent ? <NewStudentFields form={form} set={set} partners={meta?.partners || []} onCancel={() => setNewStudent(false)} />
            : <StudentSearch onPick={setPicked} onNew={() => setNewStudent(true)} />}
        </Step>
        <Step n="2" title="Application">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="College *">{sel("college_id", (meta?.colleges || []).map(c => [c.id, c.code ? `${c.code} — ${c.name}` : c.name]), "Select college", "new-app-college")}</Field>
            <Field label="Program Category *">{sel("program_category", CATEGORIES.map(c => [c, c]), "Select category", "new-app-category")}</Field>
            <Field label="Course Name"><Input data-testid="new-app-course" placeholder="e.g. Master of Data Analytics" value={form.course_name || ""} onChange={e => set("course_name", e.target.value)} /></Field>
            <Field label="Intake">{sel("intake_id", intakes.map(i => [i.id, i.name]), "Select intake", "new-app-intake")}</Field>
            <Field label="Stage">{sel("stage", STAGES.map(s => [s, s]), "Stage", "new-app-stage")}</Field>
            <Field label="Assigned Officer">{sel("assigned_admissions_officer_id", officers.map(u => [u.user_id, u.name]), "Default from B2B settings", "new-app-ao")}</Field>
          </div>
        </Step>
        <details className="rounded-lg border border-slate-200 px-4 py-2">
          <summary className="text-xs font-medium text-slate-600 cursor-pointer" data-testid="new-app-more">More details (campus, college app ID, priority, BDM)</summary>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-3">
            <Field label="Campus"><Input data-testid="new-app-campus" value={form.campus || ""} onChange={e => set("campus", e.target.value)} /></Field>
            <Field label="College Application ID"><Input data-testid="new-app-college-app-id" value={form.college_app_id || ""} onChange={e => set("college_app_id", e.target.value)} /></Field>
            <Field label="Priority">{sel("priority", PRIORITIES.map(p => [p, p]), "Medium", "new-app-priority")}</Field>
            <Field label="Assigned BDM">{sel("assigned_bdm_id", bdms.map(u => [u.user_id, u.name]), "Default from B2B settings", "new-app-bdm")}</Field>
          </div>
        </details>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="new-app-submit" onClick={() => submit()} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white">{saving ? "Creating…" : "Create Application"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StudentSearch({ onPick, onNew }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);
  useEffect(() => {
    if (q.trim().length < 2) { setRows(null); return; }
    const t = setTimeout(() => { api.get("/b2b/students", { params: { q } }).then(r => setRows(r.data.slice(0, 8))); }, 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
        <Input data-testid="new-app-student-search" className="pl-9" placeholder="Search student by name, email, phone or ID…" value={q} onChange={e => setQ(e.target.value)} />
      </div>
      {rows && (
        <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 max-h-56 overflow-y-auto">
          {rows.map(s => (
            <button key={s.id} type="button" data-testid={`new-app-student-option-${s.id}`} onClick={() => onPick(s)} className="w-full text-left px-3 py-2 hover:bg-orange-50 text-sm">
              <span className="font-medium text-slate-900">{s.student_name}</span> <span className="mono text-xs text-slate-500">{s.student_code}</span>
              <div className="text-xs text-slate-500 truncate">{s.email || "No email"} · {s.app_counts?.total || 0} application(s)</div>
            </button>
          ))}
          {rows.length === 0 && <div className="px-3 py-2 text-xs text-slate-500">No matching student.</div>}
        </div>
      )}
      <Button type="button" variant="outline" size="sm" data-testid="new-app-new-student-btn" onClick={onNew}><UserPlus className="w-4 h-4 mr-1" /> Student not found — create new</Button>
    </div>
  );
}

const StudentChip = ({ s, onClear }) => (
  <div data-testid="new-app-student-chip" className="flex items-center justify-between gap-2 rounded-lg border border-orange-200 bg-orange-50 px-4 py-2 text-sm text-orange-900">
    <span className="min-w-0 truncate"><b>{s.student_name}</b> <span className="mono text-xs">{s.student_code}</span> {s.email && `· ${s.email}`}{s.partner_name && <> · Partner <b data-testid="new-app-student-partner">{s.partner_name}</b></>}</span>
    {onClear && <button type="button" data-testid="new-app-student-clear" onClick={onClear} className="shrink-0"><X className="w-4 h-4" /></button>}
  </div>
);

function NewStudentFields({ form, set, partners, onCancel }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 rounded-lg border border-dashed border-orange-300 p-3">
      <Field label="Student Full Name *"><Input data-testid="new-app-name" value={form.student_name || ""} onChange={e => set("student_name", e.target.value)} /></Field>
      <Field label="Email"><Input data-testid="new-app-email" value={form.email || ""} onChange={e => set("email", e.target.value)} /></Field>
      <Field label="Phone"><Input data-testid="new-app-phone" value={form.phone || ""} onChange={e => set("phone", e.target.value)} /></Field>
      <Field label="Partner *">
        <Select value={form.partner_id || ""} onValueChange={v => set("partner_id", v)}>
          <SelectTrigger data-testid="new-app-partner"><SelectValue placeholder="Select partner" /></SelectTrigger>
          <SelectContent>{partners.map(p => <SelectItem key={p.id} value={p.id}>{p.company_name}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <button type="button" onClick={onCancel} data-testid="new-app-new-student-cancel" className="text-xs text-slate-500 underline text-left">Back to student search</button>
    </div>
  );
}

const Step = ({ n, title, children }) => (
  <div className="space-y-2">
    <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold"><span className="text-orange-600">{n}.</span> {title}</div>
    {children}
  </div>
);

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs text-slate-600">{label}</Label>
    {children}
  </div>
);
